/**
 * MetricTile — KPI card used at the top of every dashboard.
 *
 * Responsive: takes full width on mobile, ~quarter width when in a grid.
 * Optional `trend` prop renders a small +/- pill (color: green for positive,
 * amber for negative). `subtitle` is for secondary context like "this month".
 */

import { FunctionComponent, ReactNode } from "react";

interface Props {
  title: string;
  value: ReactNode;
  subtitle?: string;
  trend?: {
    value: number;
    suffix?: string; // e.g. "%" or " vs est"
    invertColors?: boolean; // when true, positive = bad (e.g. overruns)
  };
  icon?: ReactNode;
  accent?: "primary" | "amber" | "rose" | "green" | "blue" | "neutral";
}

const accentMap: Record<string, string> = {
  primary: "border-l-myPrimaryColor",
  amber: "border-l-amber-500",
  rose: "border-l-rose-500",
  green: "border-l-green-500",
  blue: "border-l-blue-500",
  neutral: "border-l-gray-400",
};

const MetricTile: FunctionComponent<Props> = ({
  title,
  value,
  subtitle,
  trend,
  icon,
  accent = "primary",
}) => {
  const trendIsPositive = trend ? trend.value >= 0 : false;
  const trendIsGood = trend?.invertColors
    ? !trendIsPositive
    : trendIsPositive;
  return (
    <div
      className={`bg-white rounded-md shadow-sm border-l-4 ${accentMap[accent]} p-4 flex flex-col gap-1 min-w-0`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs uppercase tracking-wide text-gray-500 truncate">
          {title}
        </span>
        {icon}
      </div>
      <div className="text-xl md:text-2xl font-semibold text-gray-900 break-words">
        {value}
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-gray-500 truncate">
          {subtitle || " "}
        </span>
        {trend && (
          <span
            className={`text-xs px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${
              trendIsGood
                ? "bg-green-50 text-green-700"
                : "bg-rose-50 text-rose-700"
            }`}
          >
            {trendIsPositive ? "+" : ""}
            {trend.value.toFixed(1)}
            {trend.suffix || "%"}
          </span>
        )}
      </div>
    </div>
  );
};

export default MetricTile;
